'use client';

import { ComingSoonBanner } from "@/components/layout/ComingSoonBanner";
import { FirebaseClientProvider } from "@/firebase/client-provider";
import { UserProvider } from "@/context/UserContext";
import { Toaster } from "@/components/ui/toaster";
import { CookieConsentBanner } from "@/components/layout/CookieConsentBanner";
import { AnalyticsProvider } from "@/components/analytics/AnalyticsProvider";
import { FloatingWhatsAppButton } from "@/components/common/FloatingWhatsAppButton";
import { getZamziApp } from "@/lib/firebase/services/zamziAppsService.client";
import { getApp } from 'firebase/app';
import { ZamziApp } from '@/types/zamziApp';
import { useEffect, useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { AppHeader } from "@/components/layout/AppHeader";
import { Footer } from "@/components/layout/Footer";
import { VersionGuard } from "@/components/system/VersionGuard";

function MaintenanceBanner() {
  // FIX: Re-introduce useSearchParams to read the URL for the bypass flag.
  const searchParams = useSearchParams();
  const isBypassed = searchParams?.get('bypass') === 'true';

  const isDev = process.env.NODE_ENV === 'development';
  
  const [appIsInMaintenance, setAppIsInMaintenance] = useState(false);

  useEffect(() => {
    const fetchAppStatus = async () => {
      try {
        const app = getApp();
        const appId = app.options.appId || "";

        const zamziApp = await getZamziApp("zamzi-connect");

        if (zamziApp) {
          setAppIsInMaintenance(zamziApp.isInMaintenance);
        }
      } catch (error) {
        console.error("Failed to fetch app maintenance status:", error);
      }
    };

    fetchAppStatus();
  }, []);
  
  // FIX: Restore the bypass logic. If maintenance is on AND the bypass is NOT active, show the banner.
  if (appIsInMaintenance && !isBypassed) {
    return <ComingSoonBanner showX={isDev} />;
  }
  
  return null;
}

export function LayoutContent({ children }: { children: React.ReactNode }) {
    // Canonical tag for parameterized URLs
    const searchParams = typeof window !== 'undefined' ? window.location.search : '';
    const hasSpammyParam =
      searchParams.includes('aiketo=') ||
      searchParams.includes('aicbd=') ||
      searchParams.includes('aimale=');
  useEffect(() => {
    // include a changing value so we can tell whether a given deployment
    // has actually reached production. NEXT_PUBLIC_APP_VERSION is set via
    // env during build; fall back to a timestamp in development.
    const version = process.env.NEXT_PUBLIC_APP_VERSION || new Date().toISOString();
    console.log(`ZAMZI VERSION ${version} - FIREBASE LIVE`);
  }, []);

  return (
    <>
      {/* Canonical tag for spammy query parameters */}
      {hasSpammyParam && (
        <link rel="canonical" href="https://zamzi.co.za/" />
      )}
      {/* The main site content is always rendered */}
      <FirebaseClientProvider>
        <UserProvider>
                    <div className="flex flex-col min-h-screen bg-white">
                      {/* Skip-to-content link: invisible until focused, allows keyboard/screen-reader
                          users to jump past the navigation directly into the page content.
                          This is the primary fix for the "Accessibility tree is not well-formed" audit. */}
                      <a
                        href="#main-content"
                        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-[#001489] focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#001489]"
                      >
                        Skip to main content
                      </a>
                      <AppHeader />
                      <main id="main-content" aria-label="Main content" className="grow container mx-auto px-4 py-8">
                        {children}
                      </main>
                      <Footer />
                    </div>
          <Toaster />
          <VersionGuard />
          <CookieConsentBanner />
          <AnalyticsProvider />
          <FloatingWhatsAppButton />
        </UserProvider>
        {/* FIX: Wrap MaintenanceBanner in Suspense as it now uses useSearchParams */}
        <Suspense fallback={null}>
          <MaintenanceBanner />
        </Suspense>
      </FirebaseClientProvider>
    </>
  );
}
